<script>
  import { _ } from '@sveltia/i18n';
  import { Menu, MenuButton, MenuItemCheckbox, MenuItemRadio } from '@sveltia/ui';

  /**
   * @import { AssetListView, EntryListView } from '$lib/types/private';
   * @import { ViewFilter } from '$lib/types/public';
   */

  /**
   * @typedef {object} Props
   * @property {{ current: EntryListView | AssetListView }} currentView Current view details.
   * @property {string} aria-controls The `aria-controls` attribute for the menu.
   * @property {string} [label] Menu button label.
   * @property {boolean} [disabled] Whether to disable the button.
   * @property {boolean} [multiple] Whether to allow selecting multiple filter conditions.
   * @property {string} [noneLabel] Label to be displayed on the None item.
   * @property {ViewFilter[]} [filters] Filter conditions.
   */

  /** @type {Props} */
  let {
    /* eslint-disable prefer-const */
    currentView,
    'aria-controls': ariaControls,
    label = '',
    disabled = false,
    multiple = false,
    noneLabel = '',
    filters = [],
    /* eslint-enable prefer-const */
  } = $props();
</script>

<MenuButton variant="ghost" label={label || _('filter')} {disabled} popupPosition="bottom-right">
  {#snippet popup()}
    <Menu aria-label={_('filtering_options')} aria-controls={ariaControls}>
      {#if multiple}
        {#each filters as filter (`${filter.field}|${String(filter.pattern)}`)}
          {@const { label: _label, field, pattern } = filter}
          {@const index = (currentView.current.filters || []).findIndex(
            (f) => f.field === field && String(f.pattern) === String(pattern),
          )}
          <MenuItemCheckbox
            label={_label}
            checked={index > -1}
            onChange={() => {
              const view = currentView.current;
              const updatedFilters = view.filters ? [...view.filters] : [];

              if (index > -1) {
                updatedFilters.splice(index, 1);
              } else {
                updatedFilters.push({ field, pattern });
              }

              currentView.current = { ...view, filters: updatedFilters };
            }}
          />
        {/each}
      {:else}
        <MenuItemRadio
          label={noneLabel || _('sort_keys.none')}
          checked={!currentView.current.filter}
          onSelect={() => {
            currentView.current = {
              ...currentView.current,
              filter: undefined,
            };
          }}
        />
        {#each filters as filter (`${filter.field}|${String(filter.pattern)}`)}
          {@const { label: _label, field, pattern } = filter}
          <MenuItemRadio
            label={_label}
            checked={currentView.current.filter?.field === field &&
              String(currentView.current.filter.pattern) === String(pattern)}
            onSelect={() => {
              currentView.current = {
                ...currentView.current,
                filter: { field, pattern },
              };
            }}
          />
        {/each}
      {/if}
    </Menu>
  {/snippet}
</MenuButton>
